import React, { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { getTimeZone } from 'react-native-localize'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { NavigationContainer } from '@src/screens/Common/containers'
import {
  EhrAppointmentKind,
  EhrAppointmentProviderKind,
  EhrGetHealthieInstanceKind,
} from '@src/types'
import { userSelector } from '@src/selectors/app'
import { NavigatorContext, useGoBack, useSnack } from '@src/utils'
import { ErrorMessages } from '@src/config'
import {
  AppStackParamList,
  CommonAuthorizedStackParamList,
  CommonStackParamList,
  OnboardingStackParamsList,
} from '@src/navigation/types'
import { Button, Text } from '@src/components/base'
import { LoadingContainer } from '@src/screens/Common/containers/LoadingContainer'
import { useShouldShowPurchaseFlow } from '@src/utils/hooks'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { EhrAvailableDatesWithSlotsQuery } from '../graphql/fetchEhrAvailableDatesWithSlots.generated'
import { EhrBookAppointmentMutation } from '../graphql/bookAppointment.generated'
import { SLOT_TAKEN_ERROR } from '../constants'
import { CoachInfoCardContent } from './CoachInfo/CoachInfoCardContent'

const APPOINTMENT_KIND = EhrAppointmentKind.Initial_60

export const ReferralVideoCallNotLicensed = () => {
  const styles = useStyleSheet(themedStyles)

  const shouldShowPurchaseFlow = useShouldShowPurchaseFlow()

  const dispatch = useDispatch()
  const user = useSelector(userSelector)
  const navigation = useNavigation<
    StackNavigationProp<AppStackParamList | OnboardingStackParamsList>
  >()
  const appStackNavigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const commonNavigation = useNavigation<StackNavigationProp<CommonStackParamList>>()
  const authorizedStackNavigation = useNavigation<
    StackNavigationProp<CommonAuthorizedStackParamList>
  >()
  const route = useRoute<
    | RouteProp<AppStackParamList, 'ReferralVideoCallNotLicensed'>
    | RouteProp<OnboardingStackParamsList, 'ReferralVideoCallNotLicensed'>
  >()
  const insets = useSafeAreaInsets()
  const goBack = useGoBack()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const bookingRef = useRef(false)

  const { meetingAt } = route.params

  const [loading, setLoading] = useState<boolean>(true)
  const [provider, setProvider] = useState<
    EhrAvailableDatesWithSlotsQuery['ehrAvailableDatesWithSlots']['providers'][number] | undefined
  >(undefined)
  const [hasMatchingTimeSlot, setHasMatchingTimeSlot] = useState(false)

  const showSnack = useSnack()

  const timeZone = user?.timeZone || getTimeZone()

  useEffect(() => {
    dispatch({
      type: 'nutritionistHub/fetchEhrAvailableDatesWithSlots',
      payload: {
        appointmentKind: APPOINTMENT_KIND,
        providerKind: EhrAppointmentProviderKind.Coach,
        getHealthieInstanceKind: EhrGetHealthieInstanceKind.BerryStreet,
        timeZone,
        excludedProvidersHealthieIds: [],
      },
      success: (response: EhrAvailableDatesWithSlotsQuery) => {
        const firstSlot = response.ehrAvailableDatesWithSlots.dates[0]?.slots[0]
        let timeSlot = firstSlot as typeof firstSlot | undefined

        const matchingTimeSlot = response.ehrAvailableDatesWithSlots.dates
          .flatMap((date) => date.slots)
          .find((slot) => slot.time === meetingAt)

        if (matchingTimeSlot) {
          timeSlot = matchingTimeSlot
          setHasMatchingTimeSlot(true)
        }

        const providerId = timeSlot?.providerId
        const provider = (response.ehrAvailableDatesWithSlots.providers.find(
          (provider) => provider.__typename === 'Coach' && provider.id === providerId,
        ) || response.ehrAvailableDatesWithSlots.providers[0]) as
          | typeof response.ehrAvailableDatesWithSlots.providers[0]
          | undefined

        setProvider(provider)
        setLoading(false)

        if (!provider) {
          showSnack('No available nutritionist found.', null, 'error')
          if (navigation.canGoBack()) {
            goBack()
          }
        }
      },
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
        if (navigation.canGoBack()) {
          goBack()
        }
        setLoading(false)
      },
    })
  }, [dispatch, goBack, meetingAt, navigation, showSnack, timeZone])

  useEffect(
    () => () => {
      if (bookingRef.current) {
        disableLoader()
        bookingRef.current = false
      }
    },
    [disableLoader],
  )

  useLazyScreenPreload('CoachInfoModal')
  useLazyScreenPreload('VideoCallConfirmed')

  const openBio = () => {
    if (!provider || !('coachId' in provider)) {
      return
    }

    const { photo, fullName, credentials, bio, specialties } = provider

    commonNavigation.navigate('CoachInfoModal', {
      variant: 'bio',
      photo,
      fullName,
      credentials,
      bio,
      specialties,
    })
    return
  }

  const onContinuePress = () => {
    if (!provider || !('coachId' in provider)) {
      return
    }

    if (!hasMatchingTimeSlot) {
      navigation.replace('ReferralVideoCall', {
        coachUserId: provider.coachId,
        status: 'coach_not_licensed',
      })
      return
    }

    if (bookingRef.current) {
      return
    }

    bookingRef.current = true
    enableLoader()

    dispatch({
      type: 'nutritionistHub/bookAppointment',
      payload: {
        appointmentKind: APPOINTMENT_KIND,
        timeZone,
        date: meetingAt,
        coachId: provider.id,
      },
      success: (response: EhrBookAppointmentMutation['ehrBookAppointment']) => {
        dispatch({ type: 'nutritionistHub/fetchUpcomingEhrAppointments' })
        dispatch({ type: 'nutritionistHub/fetchEhrSuggestedAvailability' })

        if (response.meetingAt) {
          authorizedStackNavigation.replace('VideoCallConfirmed', {
            meetingAt: response.meetingAt,
            coachFullName: provider.fullName,
            coachPhoto: provider.photo,
            coachCredentials: provider.credentials,
            coachBio: provider.bio,
            coachSpecialties: provider.specialties,
            info: 'We booked you with another nutritionist that is licensed in your state.',
          })

          dispatch({ type: 'uiFlags/fetch' })
          dispatch({ type: 'users/fetch' })
          return
        }

        if (navigation.canGoBack()) {
          goBack()
        }
      },
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        if (errorMessage.includes(SLOT_TAKEN_ERROR)) {
          appStackNavigation.replace('ReferralVideoCall', {
            coachUserId: provider.coachId,
            status: 'slot_not_available',
          })
          return
        }
        showSnack(errorMessage, null, 'error')
      },
      complete: () => {
        disableLoader()
        bookingRef.current = false
      },
    })
  }

  const goToNutritionistHub = () => {
    appStackNavigation.reset({
      index: 0,
      routes: [
        {
          name: 'Drawer',
          params: { screen: 'Dashboard', params: { screen: 'NutritionistHub' } },
        },
      ],
    })
  }

  return (
    <NavigationContainer
      title="Let’s find you a replacement"
      goBack={navigation.canGoBack() ? goBack : undefined}
      style={styles.container}
    >
      <ScrollView
        style={styles.container}
        bounces={false}
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
      >
        <Text type="large" style={styles.title}>
          We are very sorry but the nutritionist you previously selected is not licensed in your
          state.
        </Text>
        {loading && <LoadingContainer />}
        {provider && 'coachId' in provider && (
          <>
            <Text type="large" style={styles.nutritionistDescription}>
              Instead, we have matched you with the nutritionist below who is a great option for you
              and is aligned with your goals.
            </Text>
            <CoachInfoCardContent
              openBio={openBio}
              fullName={provider.fullName}
              bio={provider.bio}
              photo={provider.photo}
              credentials={provider.credentials}
              style={styles.nutritionistInfoCard}
            />
            <View style={styles.bottomContainer}>
              <Text type="large" style={styles.continueHint}>
                Click continue below to move forward with your new nutritionist option!
              </Text>
              <Button
                type="primary"
                size="block"
                accessibilityLabel="Continue"
                onPress={onContinuePress}
              >
                Continue
              </Button>

              {!shouldShowPurchaseFlow && (
                <Button
                  type="transparent"
                  size="block"
                  accessibilityLabel="Go to Nutritionist Hub"
                  onPress={goToNutritionistHub}
                >
                  Go to Nutritionist Hub
                </Button>
              )}
            </View>
          </>
        )}
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  content: {
    flex: 1,
    backgroundColor: 'theme.background',
    paddingHorizontal: 16,
  },
  container: {
    backgroundColor: 'theme.background',
    flex: 1,
  },
  title: {
    marginTop: 16,
    textAlign: 'center',
  },
  nutritionistDescription: {
    textAlign: 'center',
    marginTop: 32,
  },
  nutritionistInfoCard: {
    marginTop: 32,
    borderColor: 'theme.surface.base2',
  },
  bottomContainer: {
    flexGrow: 1,
    gap: 16,
    justifyContent: 'flex-end',
  },
  continueHint: {
    textAlign: 'center',
    color: 'theme.text.secondary',
    marginBottom: 24,
  },
})
